import { useEffect, useMemo, useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  X,
  ChevronRight,
  Utensils,
  Store,
  ShoppingCart,
  Flame,
  ShoppingBag,
  Scissors,
  Wrench,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import {
  AnywhereIllustration,
  EcosystemIllustration,
} from "@/components/illustrations/FintechIllustrations";
import TerminalImage from "@/components/TerminalImage";
import { TERMINALS, BRANDS, CATEGORY_LABEL } from "@/lib/terminals";

/* ────────────────────────────────────────────────────────────
   Brand tab — full-width tab with downward arrow when active.
   Sits at the TOP of the catalog container.
   ──────────────────────────────────────────────────────────── */
function BrandTab({ brand, isActive, onClick, isFirst, isLast }) {
  return (
    <button
      type="button"
      onClick={() => onClick(brand.id)}
      data-testid={`brand-tab-${brand.id}`}
      role="tab"
      aria-selected={isActive}
      className={`relative flex-1 text-center py-6 sm:py-7 px-4 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-[var(--swipe-accent)]/40 ${
        isActive ? "text-white" : "text-[var(--swipe-navy)] hover:text-[var(--swipe-accent)] bg-white"
      } ${!isLast ? "border-r border-line" : ""} ${isFirst && !isActive ? "rounded-tl-3xl" : ""} ${isLast && !isActive ? "rounded-tr-3xl" : ""}`}
      style={
        isActive
          ? {
              background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)",
              borderRight: "none",
            }
          : undefined
      }
    >
      <span className="font-display-tight text-[1.05rem] sm:text-[1.25rem] font-extrabold tracking-tight uppercase">
        {brand.name}
      </span>

      {/* Downward triangle pointing into the grid */}
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 -bottom-[10px] w-0 h-0"
          style={{
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderTop: "10px solid #1e90ff",
          }}
        />
      )}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────
   Terminal card — centered (image top, name & desc
   centered, single "Get Pricing" button at the bottom).
   ──────────────────────────────────────────────────────────── */
function TerminalCard({ terminal, onOpen }) {
  return (
    <div
      data-testid={`terminal-card-${terminal.slug}`}
      className="group relative bg-white border border-line rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] hover:shadow-[0_30px_60px_-25px_rgba(0,114,255,0.30)] flex flex-col text-center"
    >
      <button
        type="button"
        onClick={() => onOpen(terminal)}
        className="relative aspect-[4/3] overflow-hidden focus:outline-none"
        aria-label={`Open ${terminal.name} details`}
      >
        <TerminalImage
          terminal={terminal}
          className="transition-transform duration-500 group-hover:scale-105"
        />
      </button>

      <div className="px-6 pt-6 pb-7 flex-1 flex flex-col items-center">
        <h3 className="font-display-tight text-[20px] font-extrabold text-[var(--swipe-navy)] leading-tight">
          {terminal.name}
        </h3>
        <p className="mt-3 text-[14px] text-slate-600 leading-[1.7] line-clamp-2 min-h-[3rem]">
          {terminal.tagline}
        </p>

        <button
          type="button"
          onClick={() => onOpen(terminal)}
          className="mt-auto pt-7 w-full"
          data-testid={`get-pricing-${terminal.slug}`}
        >
          <span className="block w-full px-5 py-3 rounded-xl text-[13px] font-bold text-white tracking-wide uppercase transition-all duration-300 group-hover:shadow-[0_14px_28px_-10px_rgba(0,114,255,0.55)]" style={{
            background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)",
          }}>
            Get Pricing
          </span>
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Detail modal — image left, content right with all sections.
   ──────────────────────────────────────────────────────────── */
function TerminalModal({ terminal, onClose }) {
  useEffect(() => {
    if (!terminal) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [terminal, onClose]);

  if (!terminal) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="terminal-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[var(--swipe-navy)]/70 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-5xl bg-white sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white border border-line text-[var(--swipe-navy)] flex items-center justify-center hover:bg-[var(--swipe-navy)] hover:text-white hover:border-[var(--swipe-navy)] transition-all shadow"
          data-testid="terminal-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto">
          <div className="grid lg:grid-cols-[1fr,1.1fr] gap-0">
            <div
              className="relative h-[300px] sm:h-[400px] lg:h-auto lg:min-h-[560px]"
              style={{ background: "linear-gradient(180deg, #EAF3FF 0%, #EEF2F6 100%)" }}
            >
              <TerminalImage terminal={terminal} className="!h-full p-6" />
              <div className="absolute bottom-5 left-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 backdrop-blur text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--swipe-navy)] shadow">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)]" />
                {CATEGORY_LABEL[terminal.category]}
              </div>
            </div>

            <div className="p-7 sm:p-9 lg:p-12">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)]">
                {terminal.brandName}
              </div>
              <h2 className="mt-2 font-display-tight text-[1.85rem] sm:text-[2.25rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
                {terminal.name}
              </h2>
              <p className="mt-4 text-[16px] text-slate-600 leading-[1.7]">{terminal.tagline}</p>

              <div className="mt-7">
                <Link
                  to="/contact"
                  className="btn-primary-swipe text-[14px] py-3 px-6"
                  data-testid="terminal-modal-cta-request"
                >
                  Request This Terminal <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="mt-9 space-y-7">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-2">
                    Description
                  </div>
                  <p className="text-[15px] text-slate-700 leading-[1.8]">{terminal.description}</p>
                </div>
                <div className="border-t border-line pt-7">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-3">
                    Key Features
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2.5">
                    {terminal.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-[14px] text-slate-700 bg-[var(--swipe-accent-soft)]/50 border border-[var(--swipe-accent)]/15 rounded-lg px-3 py-2"
                      >
                        <Check className="w-4 h-4 mt-0.5 text-[var(--swipe-accent)] shrink-0" strokeWidth={3} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                {terminal.bestFor && (
                  <div className="border-t border-line pt-7">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-2">
                      Best For
                    </div>
                    <p className="text-[15px] text-slate-700 leading-[1.8]">{terminal.bestFor}</p>
                  </div>
                )}
                <div className="border-t border-line pt-7">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-3">
                    Specifications
                  </div>
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {terminal.specs.map(([k, v]) => (
                      <div key={k} className="flex flex-col py-2 border-b border-line/60">
                        <dt className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">{k}</dt>
                        <dd className="mt-0.5 text-[14px] font-semibold text-[var(--swipe-navy)]">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   MAIN PAGE — 3-tab brand catalog. Valor is the default tab.
   ──────────────────────────────────────────────────────────── */
export default function Terminals() {
  const [activeBrand, setActiveBrand] = useState("valor");
  const [openTerminal, setOpenTerminal] = useState(null);

  const brand = useMemo(() => BRANDS.find((b) => b.id === activeBrand), [activeBrand]);
  const brandTerminals = useMemo(
    () => TERMINALS.filter((t) => t.brand === activeBrand),
    [activeBrand]
  );

  return (
    <div data-testid="terminals-page">
      <Seo {...getSeoForPath("/products")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField
          density={36}
          connectDistance={130}
          className="absolute inset-0 w-full h-full"
        />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-white">Products</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Terminals</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Products
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.75rem] lg:text-[5rem] xl:text-[5.5rem] font-extrabold leading-[1.02] text-white uppercase tracking-[-0.035em]">
                  Credit Card Machines &amp; <span className="text-[var(--swipe-accent)]">Payment Terminals</span>
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  Standalone credit card machines, card readers, and payment terminals
                  built for secure, dependable checkout.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="btn-primary-swipe text-[15px] py-3.5 px-7"
                    data-testid="terminals-hero-cta-quote"
                  >
                    Request a Quote <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/savings-analysis"
                    className="btn-outline-light text-[15px] py-3.5 px-7"
                    data-testid="terminals-hero-cta-savings"
                  >
                    Free Savings Analysis
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <AnywhereIllustration
                  transparent
                  className="relative w-full h-auto max-w-[560px] mx-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTRO — centered headline + paragraph with reveal animations */}
      <section className="relative bg-white overflow-hidden">
        {/* Subtle accent backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(234,243,255,0.9) 0%, transparent 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-[200px] rounded-full pointer-events-none opacity-40"
          style={{ background: "rgba(0,114,255,0.10)", filter: "blur(80px)" }}
        />
        <div className="container-swipe relative pt-8 pb-12 lg:pt-10 lg:pb-16 text-center">
          <Reveal
            variant="words"
            as="h2"
            className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.08] tracking-[-0.02em] max-w-5xl mx-auto"
            threshold={0.05}
          >
            {"Innovative Payment Technology & Solutions".split(" ").map((word, i, arr) => (
              <span key={i}>
                <span className="word"><span>{word}</span></span>
                {i < arr.length - 1 ? " " : null}
              </span>
            ))}
          </Reveal>

          {/* Animated accent underline */}
          <Reveal variant="underline" delay={500}>
            <span
              aria-hidden="true"
              className="block mx-auto mt-6 h-[3px] w-24 rounded-full"
              style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 100%)" }}
            />
          </Reveal>

          <Reveal delay={650}>
            <p className="mt-6 text-[16px] md:text-[17px] text-slate-600 leading-[1.8] max-w-[900px] mx-auto">
              Swipetronics offers advanced payment solutions and value-added services. Our POS
              systems offer the flexibility to accept payments anytime, anywhere. Check out our
              full list of products to help your business grow smarter, faster, and more profitably.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CHOOSE THE RIGHT TERMINAL — illustration LEFT, copy RIGHT */}
      <section className="bg-white">
        <div className="container-swipe py-16 lg:py-20">
          <div className="grid lg:grid-cols-[1fr,1.1fr] gap-12 lg:gap-16 items-center">
            <Reveal variant="slide-left">
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute -inset-4 rounded-[40px] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 70% at 30% 50%, rgba(0,114,255,0.18) 0%, transparent 70%)",
                  }}
                />
                <ParticleField
                  density={24}
                  connectDistance={110}
                  className="absolute inset-0 w-full h-full rounded-[40px]"
                />
                <EcosystemIllustration className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
              </div>
            </Reveal>

            <div>
              <Reveal>
                <div className="overline mb-4">Choose the Right Terminal</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Countertop, wireless, mobile &amp; e-commerce — built for how you sell.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Explore countertop, wireless, mobile, and e-commerce payment terminals designed
                  to support seamless payments and a better customer experience. Swipetronics helps
                  businesses manage sales, process payments, and operate more efficiently with
                  reliable all-in-one terminal solutions. For integrated setups, see our{" "}
                  <Link
                    to="/pos-systems"
                    className="text-[var(--swipe-accent)] underline underline-offset-2 hover:text-[#005FE0]"
                    data-testid="products-link-pos-systems"
                  >
                    all-in-one POS systems
                  </Link>
                  .
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-7 flex flex-wrap gap-2.5">
                  {["Countertop", "Wireless", "Mobile", "E-commerce"].map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] text-[12px] font-bold uppercase tracking-wider border border-[var(--swipe-accent)]/15"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CATALOG — single bordered container with tabs on top, grid below */}
      <section className="bg-white" data-testid={`terminals-grid-${activeBrand}`}>
        <div className="container-swipe pt-10 pb-20 lg:pt-14">
          <Reveal>
            <div className="max-w-3xl mb-8">
              <div className="overline mb-2">Browse by brand</div>
              <h2 className="font-display-tight text-[1.75rem] sm:text-[2.25rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                Terminals
              </h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-[1.75]">
                <span className="font-bold text-[var(--swipe-navy)]">{brand?.name}: </span>
                {brand?.description}
              </p>
            </div>
          </Reveal>

          {/* One connected catalog container */}
          <div className="border border-line rounded-3xl overflow-hidden bg-white shadow-[0_30px_80px_-40px_rgba(6,26,58,0.15)]">
            {/* TAB BAR — full-width tabs across the top */}
            <div
              className="flex border-b border-line"
              data-testid="brand-tabs"
              role="tablist"
              aria-label="Terminal brand tabs"
            >
              {BRANDS.map((b, i) => (
                <BrandTab
                  key={b.id}
                  brand={b}
                  isActive={activeBrand === b.id}
                  onClick={setActiveBrand}
                  isFirst={i === 0}
                  isLast={i === BRANDS.length - 1}
                />
              ))}
            </div>

            {/* PRODUCT GRID — directly under tabs, inside same container */}
            <div className="px-5 py-8 sm:px-7 sm:py-10 lg:px-10 lg:py-12">
              <div
                key={`grid-${activeBrand}`}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
              >
                {brandTerminals.map((t, i) => (
                  <Reveal key={t.slug} delay={(i % 3) * 50}>
                    <TerminalCard terminal={t} onOpen={setOpenTerminal} />
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE — internal links to industry-specific pages */}
      <section className="section bg-white" data-testid="terminals-industries-links">
        <div className="container-swipe">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-10 lg:mb-12">
              <div className="overline mb-3 inline-flex justify-center">Industries We Serve</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                Terminal setups matched to your business type.
              </h2>
              <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.75] max-w-[820px] mx-auto">
                Different businesses need different countertop and mobile terminal setups.
                See what Swipetronics recommends for your industry.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 lg:gap-4">
            {[
              { icon: Utensils,     label: "Restaurants",         to: "/industries/restaurants" },
              { icon: Store,        label: "Convenience & Delis", to: "/industries/convenience-stores-delis" },
              { icon: ShoppingCart, label: "Grocery Stores",      to: "/industries/grocery-stores" },
              { icon: Flame,        label: "Smoke Shops",         to: "/industries/smoke-shops" },
              { icon: ShoppingBag,  label: "Retail Stores",       to: "/industries/retail-stores" },
              { icon: Scissors,     label: "Salons & Spas",       to: "/industries/salons-spas" },
              { icon: Wrench,       label: "Service Businesses",  to: "/industries/service-businesses" },
            ].map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.to} delay={(i % 7) * 40}>
                  <Link
                    to={ind.to}
                    data-testid={`terminals-industry-link-${ind.to.split("/").pop()}`}
                    className="group relative block bg-white border border-line rounded-2xl p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[var(--swipe-accent)] hover:shadow-[0_18px_36px_-18px_rgba(0,114,255,0.35)]"
                  >
                    <div className="w-11 h-11 mx-auto rounded-xl bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110">
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <div className="mt-3 font-display text-[13.5px] font-bold text-[var(--swipe-navy)] leading-tight group-hover:text-[var(--swipe-accent)] transition-colors">
                      {ind.label}
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--swipe-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-40 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 60% 50% at 80% 0%, rgba(0,114,255,0.20) 0%, transparent 60%)"
        }} />
        <div className="container-swipe py-16 md:py-20 relative">
          <Reveal>
            <div className="grid lg:grid-cols-[1.4fr,1fr] gap-8 items-center">
              <div>
                <div className="overline mb-3" style={{ color: "#7DB6FF" }}>Not sure which terminal?</div>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-white leading-tight">
                  Tell us how your business runs and we&apos;ll match the right device.
                </h2>
                <p className="mt-5 text-[16px] text-white/70 leading-[1.75] max-w-2xl">
                  Counter flow, ticket size, peak hours, integrations — Swipetronics matches the right
                  device to the right workflow, never the other way around.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:justify-end">
                <Link to="/contact" className="btn-primary-swipe" data-testid="terminals-cta-contact">
                  Request a Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/savings-analysis" className="btn-outline-light" data-testid="terminals-cta-savings">
                  Free Savings Analysis
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <TerminalModal terminal={openTerminal} onClose={() => setOpenTerminal(null)} />
    </div>
  );
}
